import { SupabaseService } from './SupabaseClient';
import { postToNetlifyFunction, readNetlifyErrorMessage } from './netlifyClient';

const LEGACY_STORAGE_BASE_URL = 'https://anxtchxkbnqdzrkmmxmg.supabase.co/storage/v1/object/public/recipe-images/';
const CDN_BASE_URL = 'https://img.fady.eu/';

export class StorageService extends SupabaseService {
    private extractPathFromLegacyPublicUrl(pathSegments: string[]): string | null {
        const publicIndex = pathSegments.indexOf('public');
        if (
            publicIndex !== -1 &&
            publicIndex + 1 < pathSegments.length &&
            pathSegments[publicIndex + 1] === 'recipe-images'
        ) {
            return pathSegments.slice(publicIndex + 2).join('/');
        }

        return null;
    }

    private extractPathFromObjectUrl(pathSegments: string[]): string | null {
        const objectIndex = pathSegments.indexOf('object');
        if (objectIndex !== -1 && objectIndex + 1 < pathSegments.length) {
            return pathSegments.slice(objectIndex + 2).join('/');
        }

        return null;
    }

    public transformImageUrl(url: string | null | undefined): string | null {
        if (!url) {
            return null;
        }

        if (url.startsWith(LEGACY_STORAGE_BASE_URL)) {
            let path = url.substring(LEGACY_STORAGE_BASE_URL.length);
            if (path.startsWith('public/')) {
                path = path.substring('public/'.length);
            }
            return `${CDN_BASE_URL}${path}`;
        }
        return url;
    }

    public reverseTransformImageUrl(url: string): string {
        if (!url) {
            return url;
        }

        if (url.startsWith(CDN_BASE_URL)) {
            const filename = url.substring(CDN_BASE_URL.length);
            // We assume recipe images are in public/
            return `${LEGACY_STORAGE_BASE_URL}public/${filename}`;
        }
        return url;
    }

    public async uploadImage(file: File, directoryPrefix: string = 'public'): Promise<string> {
        const supabaseClient = await this.getClient();
        const sanitizedPrefix = directoryPrefix.replace(/^\/+|\/+$/g, '') || 'public';
        const filePath = `${sanitizedPrefix}/${Date.now()}-${file.name}`;

        const { error: uploadError } = await supabaseClient.storage.from('recipe-images').upload(filePath, file);
        if (uploadError) throw new Error(`Image upload error: ${uploadError.message}`);

        const { data } = supabaseClient.storage.from('recipe-images').getPublicUrl(filePath);
        if (!data?.publicUrl) throw new Error("Could not get public URL for image.");

        return this.transformImageUrl(data.publicUrl) ?? data.publicUrl;
    }

    /**
     * Extrait le chemin du fichier (filePath) à partir d'une URL d'image Supabase.
     * Ex: 'https://your.supabase.co/storage/v1/object/public/recipe-images/public/image.jpg' -> 'public/image.jpg'
     * @param imageUrl L'URL complète de l'image.
     * @returns Le chemin du fichier dans le bucket ou null si impossible d'extraire.
     */
    public extractFilePathFromImageUrl(imageUrl: string): string | null {
        try {
            const originalUrl = this.reverseTransformImageUrl(imageUrl);
            const url = new URL(originalUrl);
            const pathSegments = url.pathname.split('/');
            return this.extractPathFromLegacyPublicUrl(pathSegments)
                ?? this.extractPathFromObjectUrl(pathSegments);

        } catch (e) {
            // console.error("Error extracting file path from image URL:", e);
        }
        return null;
    }

    /**
     * Appelle une fonction Netlify pour supprimer l'image associée à une recette.
     * Cet appel est non bloquant.
     * @param filePath Le chemin du fichier à supprimer dans le bucket Supabase.
     */
    public async triggerImageDelete(filePath: string) {
        try {
            const response = await postToNetlifyFunction({
                functionName: 'delete-recipe-image',
                payload: { filePath },
                tokenHeaderName: 'x-client-token',
                missingTokenStrategy: 'allow-empty',
            });

            if (!response) {
                return;
            }

            if (!response.ok) {
                const detail = await readNetlifyErrorMessage(
                    response,
                    response.statusText || "Erreur inconnue"
                );
                console.error(
                    "Erreur Netlify lors de la suppression d'image:",
                    response.status,
                    response.statusText,
                    detail
                );
            }
        } catch (err) {
            // console.error("Erreur lors du déclenchement de la fonction Netlify de suppression d'image:", err);
        }
    }
}

export const storageService = new StorageService();
